import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Button,
  message,
  Space,
  Divider,
  Tag
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
// 引入 wangEditor
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import ImageUpload from '../../components/ImageUpload';

const { Option } = Select;

const NewsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const editorRef = useRef(null);
  const [tagInput, setTagInput] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [tags, setTags] = useState([]);
  const [content, setContent] = useState('<p><br></p>');
  const [editor, setEditor] = useState(null);

  // 计算标签显示
  const tagsDisplay = useMemo(() => {
    return tags.map(tag => (
      <Tag key={tag} closable onClose={(e) => {
        e.preventDefault();
        removeTag(tag);
      }}>
        {tag}
      </Tag>
    ));
  }, [tags]);



  useEffect(() => {
    if (id) {
      fetchNewsDetail();
    }
  }, [id]);

  const fetchNewsDetail = async () => {
    try {
      const response = await api.get(`/admin/news/${id}`);
      if (response.code === 200) {
        const news = response.data;
        // 使用dayjs处理日期
        const publishTime = news.publishTime ? dayjs(news.publishTime) : null;
        
        // 安全处理 tags 数据
        let tagsArray = [];
        if (Array.isArray(news.tags)) {
          tagsArray = news.tags;
        } else if (typeof news.tags === 'string') {
          try {
            tagsArray = JSON.parse(news.tags);
          } catch (e) {
            tagsArray = [news.tags];
          }
        } else if (news.tags) {
          tagsArray = [news.tags];
        }
        
        form.setFieldsValue({
          title: news.title,
          author: news.author,
          tags: tagsArray,
          cover: news.cover,
          content: news.content,
          publishTime: publishTime,
          status: news.status === 1,
        });
        
        setTags(tagsArray);
        setCoverUrl(news.cover || '');
        setContent(news.content || '<p><br></p>');
      } else {
        message.error(response.message || '获取资讯详情失败');
      }
    } catch (error) {
      message.error('获取资讯详情失败');
    }
  };

  // 处理富文本内容变化
  const handleContentChange = (editor) => {
    const html = editor.getHtml();
    setContent(html);
    form.setFieldsValue({ content: html });
  };

  // 组件销毁时销毁编辑器
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  // 添加标签
  const addTag = () => {
    if (tagInput && tagInput.trim()) {
      const newTag = tagInput.trim();
      
      // 检查标签是否已存在
      if (!tags.includes(newTag)) {
        const updatedTags = [...tags, newTag];
        setTags(updatedTags);
        form.setFieldsValue({
          tags: updatedTags
        });
      }
      setTagInput('');
    }
  };

  // 移除标签
  const removeTag = (tagToRemove) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove);
    setTags(updatedTags);
    form.setFieldsValue({
      tags: updatedTags,
    });
  };

  // 处理封面图片变化
  const handleCoverChange = (value) => {
    setCoverUrl(value);
    form.setFieldsValue({
      cover: value,
    });
  };

  // 编辑器配置
  const editorConfig = {
    placeholder: '请输入资讯内容...',
    MENU_CONF: {
      uploadImage: {
        async customUpload(file, insertFn) {
          // 创建 FormData 对象用于上传图片
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', 'general');
          
          try {
            const response = await api.post('/common/upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
            
            if (response.code === 200 && response.data && response.data.fileUrl) {
              // 插入图片到编辑器
              insertFn(response.data.fileUrl, file.name, response.data.fileUrl);
            } else {
              message.error(response.message || '图片上传失败');
            }
          } catch (error) {
            message.error('图片上传失败: ' + (error.message || '未知错误'));
          }
        }
      }
    }
  };

  // 工具栏配置
  const toolbarConfig = {
    toolbarKeys: [
      'headerSelect',
      'bold',
      'italic',
      'underline',
      'through',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'fontSize',
      'color',
      'bgColor',
      '|',
      'insertLink',
      'uploadImage',
      '|',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      '|',
      'undo',
      'redo',
    ]
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // 处理参数，使用 state 中的 tags 而不是 values.tags
      const params = {
        ...values,
        publishTime: values.publishTime ? values.publishTime.format('YYYY-MM-DD HH:mm:ss') : null,
        status: values.status ? 1 : 0,
        tags: JSON.stringify(tags),
        cover: coverUrl || '',
      };
      
      let response;
      if (id) {
        // 更新
        response = await api.put(`/admin/news/${id}`, params);
      } else {
        // 创建
        response = await api.post('/admin/news', params);
      }
      
      if (response.code === 200) {
        message.success(id ? '更新成功' : '创建成功');
        navigate('/news');
      } else {
        message.error(response.message || (id ? '更新失败' : '创建失败'));
      }
    } catch (error) {
      message.error(error.message || (id ? '更新失败' : '创建失败'));
    } finally {
      setLoading(false);
    }
  };



  return (
    <Card 
      title={id ? '编辑资讯' : '新建资讯'}
      extra={
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => navigate('/news')}
        >
          返回列表
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: true,
          tags: [],
          content: '<p><br></p>',
        }}
      >
        <Form.Item
          label="标题"
          name="title"
          rules={[{ required: true, message: '请输入标题' }]}
        >
          <Input placeholder="请输入资讯标题" />
        </Form.Item>

        <Form.Item
          label="作者"
          name="author"
          rules={[{ required: true, message: '请输入作者' }]}
        >
          <Input placeholder="请输入作者姓名" />
        </Form.Item>

        <Form.Item label="标签">
          <Space>
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onPressEnter={(e) => {
                e.preventDefault();
                addTag();
              }}
              placeholder="输入标签后按回车添加"
              style={{ width: 200 }}
            />
            <Button onClick={(e) => {
              e.preventDefault();
              addTag();
            }}>添加</Button>
          </Space>
          <div style={{ marginTop: 8 }}>
            {tagsDisplay}
          </div>
        </Form.Item>

        <Form.Item label="封面" name="cover">
          <ImageUpload
            value={coverUrl}
            onChange={handleCoverChange}
            maxCount={1}
            accept="image/*"
            sizeLimit={5}
            placeholder="点击上传封面图片"
          />
        </Form.Item>

        <Form.Item
          label="内容（富文本）"
          name="content"
          rules={[{ required: true, message: '请输入资讯内容' }]}
        >
          <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px' }}>
            <Toolbar
              editor={editor}
              defaultConfig={toolbarConfig}
              mode="default"
              style={{ borderBottom: '1px solid #d9d9d9' }}
            />
            <Editor
              defaultConfig={editorConfig}
              value={content}
              onCreated={setEditor}
              onChange={handleContentChange}
              mode="default"
              style={{ height: '400px', overflowY: 'auto' }}
            />
          </div>
        </Form.Item>

        <Form.Item
          label="发布时间"
          name="publishTime"
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="状态"
          name="status"
          valuePropName="checked"
        >
          <Switch checkedChildren="启用" unCheckedChildren="禁用" defaultChecked />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? '更新' : '创建'}
            </Button>
            <Button onClick={() => navigate('/news')}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default NewsForm;